import { cutToast } from 'utility/toast';
import {
  ContextualMenu,
  IDragOptions,
  Modal,
  Separator,
  Stack,
  makeStyles,
} from '@fluentui/react';
import React, { useEffect } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchema } from './validations';
import { BasicForm } from './BasicForm';
import { GroupItems } from './GroupItems';
import { getDefaultValues, getDeletedItems, getNewGroupItems } from './utils';
import { useMutation } from '@apollo/client';

import { isEmpty } from 'lodash';

import WORKGROUP_GROUP_CREATE from './WorkgroupGroupCreate.graphql';
import WORKGROUP_GROUP_UPDATE from './WorkgroupGroupUpdate.graphql';
import WORKGROUP_GROUP_FIELDS from '../../WorkgroupGroupFields.graphql';
import {
  WorkgroupGroupUpdate,
  WorkgroupGroupUpdateVariables,
} from './__generated__/WorkgroupGroupUpdate';
import {
  WorkgroupGroupCreate,
  WorkgroupGroupCreateVariables,
} from './__generated__/WorkgroupGroupCreate';
import {
  WorkgroupGroupInput,
  WorkgroupGroupPatch,
  WorkgroupGroupUpdateInput,
} from 'common/types/globalTypes';
import { WorkgroupGroupRow } from '../../list';
import { WorkgroupGroupValues } from './types';
const WORKGROUP_GROUP_FRAGMENT_NAME = 'WorkgroupGroupFields';

const DragOptions: IDragOptions = {
  moveMenuItemText: 'Move',
  closeMenuItemText: 'Close',
  menu: ContextualMenu,
  dragHandleSelector: '.ms-Modal-scrollableContent > div > div:first-child ',
};

const useStyles = makeStyles(() => ({
  container: {
    width: 800,
    maxHeight: 800,
  },
}));

interface WorkgroupGroupsViewModalProps {
  isOpen: boolean;
  isEdit: boolean;
  workgroupGroup?: WorkgroupGroupRow;
  onClose: () => void;
}

export const WorkgroupGroupsViewModal: React.FC<
  WorkgroupGroupsViewModalProps
> = ({ isOpen, isEdit, workgroupGroup, onClose }) => {
  const styles = useStyles();
  const formMethods = useForm<WorkgroupGroupValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });

  const [createWorkgroupGroup, { loading: createLoading }] = useMutation<
    WorkgroupGroupCreate,
    WorkgroupGroupCreateVariables
  >(WORKGROUP_GROUP_CREATE, { errorPolicy: 'all' });

  const [updateWorkgroupGroup, { loading: updateLoading }] = useMutation<
    WorkgroupGroupUpdate,
    WorkgroupGroupUpdateVariables
  >(WORKGROUP_GROUP_UPDATE, { errorPolicy: 'all' });

  const { handleSubmit, reset, trigger } = { ...formMethods };

  const { id, _rowTimestamp } = { ...workgroupGroup };

  const onHandleSubmit = async (values: WorkgroupGroupValues) => {
    const { name, description, workgroupGroupItems } = {
      ...values,
    };

    const workgroupGroupPayload: WorkgroupGroupInput = {
      name: name || '',
      description,
    };

    if (isEdit) {
      const defaultValues = getDefaultValues({ isEdit, workgroupGroup });
      const workgroupGroupPatch: WorkgroupGroupPatch = Object.entries(
        values
      ).reduce((res, [key, val]) => {
        if (val !== defaultValues[key as keyof WorkgroupGroupValues]) {
          if (key === 'workgroupGroupItems') return { ...res };
          else return { ...res, [key]: val };
        }
        return res;
      }, {});

      const newGroupItems = getNewGroupItems({
        workgroupGroupItems: workgroupGroup?.workgroupGroupItems.nodes,
        values: workgroupGroupItems || [],
      });
      const deletedGroupItems = getDeletedItems({
        workgroupGroupItems: workgroupGroup?.workgroupGroupItems.nodes,
        values: workgroupGroupItems || [],
      });

      const { errors } = await updateWorkgroupGroup({
        variables: {
          input: {
            id,
            rowTimestamp: _rowTimestamp,
            workgroupGroupPatch: !isEmpty(workgroupGroupPatch)
              ? workgroupGroupPatch
              : undefined,
            workgroupGroupItemsCreate: newGroupItems,
            workgroupGroupItemsDelete: deletedGroupItems,
          } as WorkgroupGroupUpdateInput,
        },
      });
      if (errors?.length) cutToast.error(errors[0].message);
      else {
        cutToast.success('Workgroup group edited successfully.');
      }
    } else {
      const { errors } = await createWorkgroupGroup({
        variables: {
          input: {
            workgroupGroup: workgroupGroupPayload,
            workgroupGroupItems: workgroupGroupItems?.map((ele) => ({
              workgroupId: ele.workgroupId || '',
            })),
          },
        },
        update: (cache, { data }) => {
          if (!!data?.workgroupGroupCreate?.workgroupGroup?.id) {
            cache.modify({
              fields: {
                workgroupGroups(existing) {
                  if (data?.workgroupGroupCreate?.workgroupGroup) {
                    const newWorkgroupGroupRef = cache.writeFragment({
                      data: data.workgroupGroupCreate.workgroupGroup,
                      fragment: WORKGROUP_GROUP_FIELDS,
                      fragmentName: WORKGROUP_GROUP_FRAGMENT_NAME,
                    });
                    return {
                      ...existing,
                      nodes: [newWorkgroupGroupRef, ...existing.nodes],
                    };
                  }
                },
              },
            });
          }
        },
      });
      if (errors?.length) cutToast.error(errors[0].message);
      else {
        cutToast.success('Workgroup group created successfully.');
        onClose();
      }
    }
  };

  useEffect(() => {
    const defaultValues = getDefaultValues({ isEdit, workgroupGroup });
    reset(defaultValues);
    trigger();
  }, [workgroupGroup, isEdit, isOpen, reset, trigger]);

  const loading = createLoading || updateLoading;

  return (
    <Modal isOpen={isOpen} isBlocking dragOptions={DragOptions}>
      <Stack className={styles.container}>
        {/* START OF FORM PROVIDER */}
        <FormProvider {...formMethods}>
          {/* HEADER */}
          <Header isEdit={isEdit} onClose={onClose} />
          {/* FORM */}
          <BasicForm />
          <Separator />
          <GroupItems workgroupGroup={workgroupGroup} />
          <Separator />
          {/* FOOTER */}
          <Footer
            loading={loading}
            onSubmit={handleSubmit(onHandleSubmit)}
            onCancel={onClose}
          />
        </FormProvider>
        {/* END OF FORM PROVIDER */}
      </Stack>
    </Modal>
  );
};
